import { axiosApi } from "../../../api/dbAxios";

export type MemoJobStatus = "pending" | "running" | "done" | "error";

export interface MemoJobCreateResponse {
  job_id: string;
  status: MemoJobStatus;
  progress_message: string;
}

export interface MemoJobStatusResponse {
  id: string;
  status: MemoJobStatus;
  progress_message: string;
  download_url?: string | null;
  error_message?: string | null;
  created_at: string;
  started_at?: string | null;
  finished_at?: string | null;
}

export const createMemoJob = async (
  assistant_answer: string,
): Promise<MemoJobCreateResponse> => {
  const response = await axiosApi.post(`/memos/jobs/`, { assistant_answer });
  return response.data;
};

export const getMemoJobStatus = async (
  jobId: string,
): Promise<MemoJobStatusResponse> => {
  const response = await axiosApi.get(`/memos/jobs/${jobId}/`);
  return response.data;
};

export const getMemoJobDownloadUrl = (jobId: string): string => {
  const base = String(axiosApi.defaults.baseURL || "").replace(/\/$/, "");
  return `${base}/memos/jobs/${jobId}/download/`;
};

export const waitForMemoJob = async (
  jobId: string,
  onProgress?: (job: MemoJobStatusResponse) => void,
  intervalMs = 2000,
  timeoutMs = 20 * 60 * 1000,
): Promise<MemoJobStatusResponse> => {
  const start = Date.now();

  while (true) {
    const job = await getMemoJobStatus(jobId);
    onProgress?.(job);

    if (job.status === "done") {
      return job;
    }

    if (job.status === "error") {
      throw new Error(job.error_message || "Memo generation failed");
    }

    if (Date.now() - start > timeoutMs) {
      throw new Error("Memo generation timed out");
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
};

export const downloadMemoJob = async (jobId: string, filename = "memos.zip"): Promise<void> => {
  const url = `/memos/jobs/${jobId}/download/`;

  const response = await axiosApi.get(url, { responseType: "blob" });
  const blob = new Blob([response.data], { type: "application/zip" });
  const objectUrl = URL.createObjectURL(blob);

  try {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
  }
};
